import React, { useState, useContext, useEffect } from "react";
import { Typography, Row, Col, Card, InputNumber, Form, Alert, Slider, Collapse } from "antd";
import { ThemeContext } from "../../context/ThemeContext";
import LandingLayout from "./LandingLayout";
import {
  LineChartOutlined,
  CalculatorOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import "./YieldCalculator.css";
import "./LandingHero.css";

const { Title, Paragraph, Text } = Typography;
const { Panel } = Collapse;

const YieldCalculator = () => {
  const { isDark } = useContext(ThemeContext);

  const [inputs, setInputs] = useState({
    faceValue: 1000,
    marketValue: 980,
    couponRate: 8,
    yearsToMaturity: 5,
  });

  const [results, setResults] = useState({
    annualCoupon: null,
    currentYield: null,
    ytm: null,
    totalReturn: null,
  });

  const handleChange = (field, value) => {
    const safeValue = value ?? 0;
    const nextInputs = {
      ...inputs,
      [field]: safeValue,
    };
    setInputs(nextInputs);
    calculate(nextInputs);
  };

  const calculate = ({ faceValue, marketValue, couponRate, yearsToMaturity }) => {
    if (!faceValue || !marketValue || !yearsToMaturity) {
      setResults({
        annualCoupon: null,
        currentYield: null,
        ytm: null,
        totalReturn: null,
      });
      return;
    }

    const annualCoupon = (faceValue * (couponRate || 0)) / 100;

    const currentYield =
      marketValue > 0 ? (annualCoupon / marketValue) * 100 : null;

    // Approximate Yield to Maturity (YTM) – standard approximation formula
    // YTM ≈ [Annual Coupon + (Face Value – Market Price) / Years to Maturity] /
    //        [(Face Value + Market Price) / 2]
    let ytm = null;
    if (marketValue > 0 && yearsToMaturity > 0) {
      ytm =
        ((annualCoupon + (faceValue - marketValue) / yearsToMaturity) /
          ((faceValue + marketValue) / 2)) *
        100;
    }

    // Total Estimated Return until maturity (rough, assuming hold to maturity):
    // Total Return = Coupon Income * Years + (Face Value – Market Price)
    // Total Return % = (Total Return / Market Price) * 100
    let totalReturn = null;
    if (marketValue > 0) {
      const totalIncome =
        annualCoupon * (yearsToMaturity || 0) + (faceValue - marketValue);
      totalReturn = (totalIncome / marketValue) * 100;
    }

    setResults({
      annualCoupon,
      currentYield,
      ytm,
      totalReturn,
    });
  };

  // Calculate once on mount so default inputs also show default results
  useEffect(() => {
    calculate(inputs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <LandingLayout>
      <div className={`yield-calculator-page ${isDark ? "dark" : ""}`}>
        {/* Hero Section – aligned with IssuerCategorization hero style */}
        <div className="container">
          <div className="landing-hero-card">
            <Title level={1} className="landing-hero-title">
              <LineChartOutlined className="landing-hero-icon" />
              Bond Yield Calculator
            </Title>
            <Paragraph className="landing-hero-subtitle">
              Calculate <strong>Annual Coupon</strong>,{" "}
              <strong>Current Yield</strong>,{" "}
              <strong>Yield to Maturity (YTM)</strong> and{" "}
              <strong>Total Estimated Returns</strong> for any bond using simple,
              transparent formulas designed for modern fixed-income investors.
            </Paragraph>
          </div>
        </div>

        {/* Calculator + Results */}
        <section className="yc-main-section">
          <div className="container">
            {/* Inputs + Results in one row, two columns */}
            <Row gutter={[24, 24]} align="stretch">
              <Col xs={24} lg={16}>
                <Card
                  className="yc-card yc-input-card"
                  bordered={false}
                  title={
                    <span className="yc-card-title">
                      <CalculatorOutlined /> Bond Inputs
                    </span>
                  }
                >
                  <Form layout="vertical">
                    <Form.Item label="Face Value (₹)">
                      <div className="yc-input-row">
                        <InputNumber
                          className="yc-input"
                          min={100}
                          step={100}
                          value={inputs.faceValue}
                          onChange={(v) => handleChange("faceValue", v)}
                        />
                        <Slider
                          min={100}
                          max={200000}
                          step={100}
                          value={Math.min(
                            Math.max(inputs.faceValue, 100),
                            200000
                          )}
                          onChange={(v) => handleChange("faceValue", v)}
                        />
                      </div>
                    </Form.Item>

                    <Form.Item label="Market Value / Price (₹)">
                      <div className="yc-input-row">
                        <InputNumber
                          className="yc-input"
                          min={100}
                          step={10}
                          value={inputs.marketValue}
                          onChange={(v) => handleChange("marketValue", v)}
                        />
                        <Slider
                          min={100}
                          max={200000}
                          step={10}
                          value={Math.min(
                            Math.max(inputs.marketValue, 100),
                            200000
                          )}
                          onChange={(v) => handleChange("marketValue", v)}
                        />
                      </div>
                    </Form.Item>

                    <Form.Item label="Coupon Rate (% per year)">
                      <div className="yc-input-row">
                        <InputNumber
                          className="yc-input"
                          min={0}
                          max={20}
                          step={0.1}
                          value={inputs.couponRate}
                          onChange={(v) => handleChange("couponRate", v)}
                        />
                        <Slider
                          min={0}
                          max={20}
                          step={0.1}
                          value={Math.min(
                            Math.max(inputs.couponRate, 0),
                            20
                          )}
                          onChange={(v) => handleChange("couponRate", v)}
                        />
                      </div>
                    </Form.Item>

                    <Form.Item label="Years to Maturity">
                      <div className="yc-input-row">
                        <InputNumber
                          className="yc-input"
                          min={1}
                          max={40}
                          step={1}
                          value={inputs.yearsToMaturity}
                          onChange={(v) => handleChange("yearsToMaturity", v)}
                        />
                        <Slider
                          min={1}
                          max={40}
                          step={1}
                          value={Math.min(
                            Math.max(inputs.yearsToMaturity, 1),
                            40
                          )}
                          onChange={(v) => handleChange("yearsToMaturity", v)}
                        />
                      </div>
                    </Form.Item>

                    <Alert
                      type="info"
                      showIcon
                      className="yc-hint"
                      icon={<InfoCircleOutlined />}
                      message={
                        <span>
                          Values update <strong>automatically</strong> as you
                          adjust the sliders or numbers. Use this tool to build
                          intuition before making detailed pricing decisions.
                        </span>
                      }
                    />
                  </Form>
                </Card>
              </Col>

              <Col xs={24} lg={8}>
                <Card
                  className="yc-card yc-results-card"
                  bordered={false}
                  title={<span className="yc-card-title">Results</span>}
                >
                  <div className="yc-results-grid">
                    <div className="yc-result-item">
                      <Text className="yc-result-label">
                        Annual Coupon Payment
                      </Text>
                      <Text className="yc-result-value">
                        {results.annualCoupon != null
                          ? `₹${results.annualCoupon.toFixed(2)}`
                          : "—"}
                      </Text>
                    </div>

                    <div className="yc-result-item">
                      <Text className="yc-result-label">Current Yield</Text>
                      <Text className="yc-result-value">
                        {results.currentYield != null
                          ? `${results.currentYield.toFixed(2)}%`
                          : "—"}
                      </Text>
                    </div>

                    <div className="yc-result-item">
                      <Text className="yc-result-label">
                        Yield to Maturity (YTM)
                      </Text>
                      <Text className="yc-result-value">
                        {results.ytm != null
                          ? `${results.ytm.toFixed(2)}%`
                          : "—"}
                      </Text>
                    </div>

                    <div className="yc-result-item">
                      <Text className="yc-result-label">
                        Total Estimated Return
                      </Text>
                      <Text className="yc-result-value">
                        {results.totalReturn != null
                          ? `${results.totalReturn.toFixed(2)}%`
                          : "—"}
                      </Text>
                    </div>
                  </div>
                </Card>
              </Col>
            </Row>

            {/* Formulas Section - full width below */}
            <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
              <Col span={24}>
                <Card
                  className="yc-card yc-formula-card"
                  bordered={false}
                  title={
                    <span className="yc-card-title">
                      <InfoCircleOutlined /> Formulas &amp; Explanations
                    </span>
                  }
                >
                      <Title level={4}>
                        Bond Yield Calculator – Calculate YTM &amp; Returns Instantly
                      </Title>
                      <Paragraph className="yc-explanation">
                        For investors looking to earn steady returns from fixed-income
                        securities, a bond yield calculator is an essential tool. It helps
                        you understand key metrics like{" "}
                        <strong>Yield to Maturity (YTM)</strong>,{" "}
                        <strong>Current Yield</strong>, and{" "}
                        <strong>Total Returns</strong> before you invest.
                      </Paragraph>

                      <Paragraph className="yc-explanation">
                        This calculator uses four key inputs:
                      </Paragraph>
                      <ul className="yc-list">
                        <li>Face Value (Par Value) of the bond</li>
                        <li>Market Price of the bond</li>
                        <li>Coupon Rate (annual interest paid)</li>
                        <li>Years to Maturity</li>
                      </ul>

                      <Paragraph className="yc-explanation">
                        From these, it derives:
                      </Paragraph>
                      <ul className="yc-list">
                        <li>Annual Coupon Payment</li>
                        <li>Current Yield (return based on today&apos;s price)</li>
                        <li>Yield to Maturity (YTM)</li>
                        <li>Total Estimated Return till maturity</li>
                      </ul>

                      <div className="yc-formula-block">
                        <Title level={5}>1. Annual Coupon Payment</Title>
                        <Paragraph className="yc-formula">
                          <Text strong>Formula:</Text>{" "}
                          Annual Coupon = Face Value × Coupon Rate
                          {" / 100"}
                        </Paragraph>
                        <Paragraph className="yc-explanation">
                          This is the <strong>cash interest</strong> you receive
                          every year from the bond based on its face value.
                        </Paragraph>
                      </div>

                      <div className="yc-formula-block">
                        <Title level={5}>2. Current Yield</Title>
                        <Paragraph className="yc-formula">
                          <Text strong>Formula:</Text>{" "}
                          Current Yield = (Annual Coupon ÷ Market Price) × 100
                        </Paragraph>
                        <Paragraph className="yc-explanation">
                          Current Yield shows the{" "}
                          <strong>income return today</strong> based on the
                          price you pay in the market, ignoring maturity gains
                          or losses.
                        </Paragraph>
                      </div>

                      <div className="yc-formula-block">
                        <Title level={5}>3. Yield to Maturity (YTM)</Title>
                        <Paragraph className="yc-formula">
                          <Text strong>Formula:</Text>{" "}
                          YTM ={" "}
                          <em>
                            (C + ((F − P) ÷ N)) ÷ ((F + P) ÷ 2)
                          </em>{" "}
                          × 100
                        </Paragraph>
                        <Paragraph className="yc-explanation">
                          YTM is the <strong>annualised return</strong> if you
                          hold the bond till maturity, assuming all coupons are
                          paid on time and you reinvest at the same rate.
                        </Paragraph>
                        <Paragraph className="yc-explanation">
                          Where: C = Annual Coupon Payment, F = Face Value, P = Current
                          Market Price, N = Years to Maturity.
                        </Paragraph>
                        <Paragraph className="yc-explanation">
                          Example: For a bond with ₹1,00,000 face value, 7.5% coupon and
                          market price of ₹95,000, the YTM will be higher than the current
                          yield because you are buying at a discount.
                        </Paragraph>
                      </div>

                      <div className="yc-formula-block">
                        <Title level={5}>4. Total Estimated Return</Title>
                        <Paragraph className="yc-formula">
                          <Text strong>Formula:</Text>{" "}
                          Total Return = (Annual Coupon × Years to Maturity) +
                          (Face Value − Market Price)
                          <br />
                          Total Return % = (Total Return ÷ Market Price) × 100
                        </Paragraph>
                        <Paragraph className="yc-explanation">
                          This is a simple estimate of your{" "}
                          <strong>overall gain</strong> from coupons plus price
                          appreciation (or loss) from buying below (or above)
                          face value and holding to maturity.
                        </Paragraph>
                      </div>
                    </Card>
              </Col>
            </Row>

            {/* FAQ Section */}
            <Row gutter={[24, 24]} style={{ marginTop: 32 }}>
              <Col span={24}>
                <Card
                  className="yc-card yc-faq-card"
                  bordered={false}
                  title={
                    <span className="yc-card-title">
                      <InfoCircleOutlined /> FAQ&apos;s on Bond Yield Calculator
                    </span>
                  }
                >
                  <Collapse
                    accordion
                    bordered={false}
                    className="yc-faq-collapse"
                    defaultActiveKey={["1"]}
                  >
                    <Panel
                      header={
                        <Text strong>What is a bond in finance?</Text>
                      }
                      key="1"
                    >
                      <Paragraph className="yc-explanation">
                        A bond is a fixed-income investment or a unit of corporate or
                        government debt issued to raise funds. Bonds carry a{" "}
                        <strong>maturity date</strong> at which the principal amount
                        (face value) is repaid to the bondholder, along with periodic
                        interest (coupon) payments during the life of the bond.
                      </Paragraph>
                    </Panel>

                    <Panel
                      header={
                        <Text strong>How do bonds work?</Text>
                      }
                      key="2"
                    >
                      <Paragraph className="yc-explanation">
                        Bonds are issued by governments and companies to raise capital.
                        When you purchase a bond, you are effectively{" "}
                        <strong>lending money to the issuer</strong>. In return, the
                        issuer promises to pay you regular interest (coupon payments)
                        and repay the face value on the maturity date.
                      </Paragraph>
                    </Panel>

                    <Panel
                      header={
                        <Text strong>What is an example of a bond?</Text>
                      }
                      key="3"
                    >
                      <Paragraph className="yc-explanation">
                        Bonds come in many forms. Common examples include{" "}
                        <strong>Treasury Bonds</strong>,{" "}
                        <strong>Municipal Bonds</strong>, and{" "}
                        <strong>Corporate Bonds</strong> issued by companies. Each type
                        has its own risk and return profile depending on the issuer and
                        tenure.
                      </Paragraph>
                    </Panel>

                    <Panel
                      header={
                        <Text strong>Can I invest ₹1,000 in bonds?</Text>
                      }
                      key="4"
                    >
                      <Paragraph className="yc-explanation">
                        Yes, many platforms and bond issues allow you to start
                        investing with relatively low minimum amounts such as{" "}
                        <strong>₹1,000</strong>. Minimum ticket size depends on the
                        specific bond and platform through which you are investing.
                      </Paragraph>
                    </Panel>

                    <Panel
                      header={
                        <Text strong>How can I invest in bonds in India?</Text>
                      }
                      key="5"
                    >
                      <Paragraph className="yc-explanation">
                        You can invest in <strong>corporate bonds</strong> and{" "}
                        <strong>government securities</strong> through SEBI-regulated
                        online platforms, broking accounts, and the{" "}
                        <strong>RBI Retail Direct</strong> portal. These channels allow
                        you to browse available bonds, view yields, and place purchase
                        orders online.
                      </Paragraph>
                    </Panel>

                    <Panel
                      header={
                        <Text strong>Are bonds in India a safe investment option?</Text>
                      }
                      key="6"
                    >
                      <Paragraph className="yc-explanation">
                        Bonds are generally considered{" "}
                        <strong>safer than equities</strong> because they provide
                        fixed interest and rank higher in the payout hierarchy than
                        shares. However, safety depends on factors such as{" "}
                        <strong>credit rating, issuer quality, tenure,</strong> and{" "}
                        <strong>interest rate risk</strong>. Investors seeking higher
                        safety usually prefer bonds with strong credit ratings and
                        government or blue-chip issuers.
                      </Paragraph>
                    </Panel>
                  </Collapse>
                </Card>
              </Col>
            </Row>
          </div>
        </section>
      </div>
    </LandingLayout>
  );
};

export default YieldCalculator;


